import { FirebaseError } from '@firebase/util';
import { first, kebabCase, last } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppContext } from '../../../data/context/app-context';
import { useDefaultHealthProviderItems } from '../../../data/data/health-providers/use-default-health-provider-items';
import { useFormatters } from '../../../data/utils/formatters.utils';
import { Button, ButtonType } from '../../../elements/button/button';
import { CustomFormattedMessage } from '../../../elements/intl/custom-formatted-message';
import { PincodeInput } from '../../../elements/pincode-input/pincode-input';
import { AuthStatus } from '../../../firebase/authentication';
import { useVersionCheck } from '../../../hooks/use-version-check';

export function PinCode() {
  const intl = useIntl();

  useVersionCheck();

  const {
    user,
    status,
    verifySMSCode,
    checkPinCode,
    setPinCode,
    forgotPinCode,
  } = useAppContext();

  const [loading, setLoading] = useState<boolean>(false);
  const [pincodeLoading, setPincodeLoading] = useState<boolean>(false);
  const [initCode, setInitCode] = useState<string>('');
  const [formErrorMessage, setFormErrorMessage] = useState<string | null>(null);
  const [formSuccessMessage, setFormSuccessMessage] = useState<string | null>(
    null
  );

  const maxLength = useMemo(
    () => (status?.status === AuthStatus.UNVERIFIED ? 6 : 5),
    [status?.status]
  );

  const navigate = useNavigate();
  const location = useLocation();

  const healthProviderId = useMemo(() => {
    const firstSegment = first(
      location.pathname.replace(/^\/|\/$/g, '').split('/')
    );

    if (
      firstSegment?.match(/(([\w\d]+)-([\w\d]+)-([\w\d]+)-([\w\d]+)-([\w\d]+))/)
    ) {
      return firstSegment;
    }

    return null;
  }, [location.pathname]);

  const { data: healthProviderItemsData } = useDefaultHealthProviderItems(
    !user || status?.status !== AuthStatus.AUTHORIZED
  );

  const navigateToHomeFromPincode = useCallback(() => {
    if (!healthProviderId) {
      const healthProvider = first(healthProviderItemsData);
      navigate(healthProvider?.id ?? '/', {
        state: { from: 'pincode' },
      });
    }
  }, [healthProviderId, healthProviderItemsData, navigate]);

  const checkCode = useCallback(
    async (input: string) => {
      if (input && initCode) {
        if (input === initCode) {
          const result = await setPinCode(input, undefined, true);
          if (result.success) {
            navigateToHomeFromPincode();
          } else {
            setInitCode('');
            setFormErrorMessage(
              intl.formatMessage({
                id: `auth.errors.unknown-error`,
              })
            );
          }
        } else {
          setInitCode('');
          setFormErrorMessage(
            intl.formatMessage({
              id: `auth.errors.different-pincode`,
            })
          );
        }
      } else {
        // set init code to validate against
        setInitCode(input);
      }
    },
    [initCode, intl, navigateToHomeFromPincode, setPinCode]
  );

  const onHandleForgotPincode = useCallback(async () => {
    setLoading(true);
    setFormSuccessMessage(null);
    setFormErrorMessage(null);
    try {
      const result = await forgotPinCode();
      if (!result.success) {
        setFormErrorMessage(
          intl.formatMessage({
            id: `auth.errors.${result.message}`,
          })
        );
      } else {
        setFormSuccessMessage(
          intl.formatMessage({ id: 'auth.forgot-pincode.success' })
        );
      }
    } catch (error) {
      setFormErrorMessage((error as Error).message);
    }

    setLoading(false);
  }, [forgotPinCode, intl]);

  const onCodeComplete = useCallback(
    async (code: string) => {
      setPincodeLoading(true);
      try {
        setFormSuccessMessage(null);
        setFormErrorMessage(null);
        if (status?.status === AuthStatus.UNVERIFIED) {
          const result = await verifySMSCode(code);
          if (result.status === AuthStatus.AUTHORIZED) {
            navigateToHomeFromPincode();
          } else if (result.status !== AuthStatus.PINCODE) {
            const message = kebabCase(
              last((result.message ?? 'unknown-error').split('/'))
            );

            setFormErrorMessage(
              intl.formatMessage({
                id: `auth.errors.${message}`,
                defaultMessage: message,
              })
            );
          }
        } else if (status?.status === AuthStatus.PINCODE) {
          if (user?.hasPinCode) {
            const result = await checkPinCode(code);
            if (result.status === AuthStatus.AUTHORIZED) {
              navigateToHomeFromPincode();
            } else {
              const message = kebabCase(
                last((result.message ?? 'unknown-error').split('/'))
              );

              setFormErrorMessage(
                intl.formatMessage({
                  id: `auth.errors.${message}`,
                  defaultMessage: message,
                })
              );
            }
          } else {
            checkCode(code);
          }
        }
      } catch (error) {
        if (error instanceof FirebaseError) {
          const message = kebabCase(
            last((error.code ?? 'unknown-error').split('/'))
          );

          setFormErrorMessage(
            intl.formatMessage({
              id: `auth.errors.${message}`,
              defaultMessage: message,
            })
          );
        } else {
          setFormErrorMessage((error as Error).message);
        }
      } finally {
        setPincodeLoading(false);
        setLoading(false);
      }
    },
    [
      checkCode,
      checkPinCode,
      intl,
      navigateToHomeFromPincode,
      status,
      user,
      verifySMSCode,
    ]
  );

  const message = () => {
    if (status?.verification?.phoneNumber) {
      return (
        <CustomFormattedMessage
          id="auth.pincode.sms"
          values={{ number: status.verification.phoneNumber }}
        />
      );
    }

    if (user) {
      if (user.hasPinCode) {
        return <CustomFormattedMessage id="auth.pincode.personal" />;
      }
      if (!initCode) {
        return <CustomFormattedMessage id="auth.pincode.create" />;
      }
      return <CustomFormattedMessage id="auth.pincode.verify" />;
    }
    return '';
  };

  const { getFullName } = useFormatters();

  return (
    <>
      <h2>
        {user ? getFullName(user) : status?.verification?.phoneNumber ?? ''}
      </h2>

      <p>{message()}</p>

      {formErrorMessage && <p className="form-error">{formErrorMessage}</p>}
      {formSuccessMessage && (
        <p className="form-success">{formSuccessMessage}</p>
      )}

      <PincodeInput
        loading={pincodeLoading || loading}
        maxLength={maxLength}
        onComplete={onCodeComplete}
      />

      {user && user.hasPinCode && (
        <Button
          disabled={pincodeLoading}
          testId="forgot-pincode"
          type={ButtonType.TEXT}
          onClick={onHandleForgotPincode}
        >
          <CustomFormattedMessage id="auth.forgot-pincode" />
        </Button>
      )}
    </>
  );
}
